export interface Point {
  x: number;
  y: number;
}

/**
 * Center-anchored corner-resize scaling for cover text/design elements.
 *
 * The handle is dragged radially relative to the element's centre. The new
 * scale is `startScale × (currentDistance / startDistance)`, where both
 * distances are measured in screen pixels from the box centre.
 *
 * Because the numerator and denominator share the same screen-pixel space,
 * the result is inherently invariant to any canvas zoom/scale — the element
 * tracks the cursor 1:1 at any zoom level. This is the same gesture math the
 * cover artwork resize (`onDesignCornerDown`) already uses.
 *
 * @param center      Box centre in screen px (getBoundingClientRect centre).
 * @param start       Cursor position at mousedown, screen px.
 * @param current     Cursor position now, screen px.
 * @param startScale  The element's scale (or font size) when the drag began.
 * @param min         Lower clamp bound.
 * @param max         Upper clamp bound.
 */
export function cornerResizeScale(
  center: Point,
  start: Point,
  current: Point,
  startScale: number,
  min: number,
  max: number,
): number {
  // Guard against a zero start distance (drag started on the centre) so the
  // ratio never divides by zero.
  const startDist = Math.max(1, Math.hypot(start.x - center.x, start.y - center.y));
  const dist = Math.max(1, Math.hypot(current.x - center.x, current.y - center.y));
  const next = startScale * (dist / startDist);
  return Math.max(min, Math.min(max, next));
}
